<script setup lang="ts">
import { getMeasure } from "@lib/utils/getMeasure";
import { calculateDistance } from "@lib/utils/calculateDistance";
import { formatDate } from "@lib/utils/formatDate";
import { formatCurrency } from "@lib/utils/formatCurrency";
import {
    CheckCircle,
    Clock,
    Info,
    MapPin,
    MoreHorizontal,
    Navigation,
    XCircle,
} from "lucide-vue-next";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogScrollContent, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { deliveryteamOrdersApiClient } from "@api-client/deliveryteam_orders";
import type { ResType } from "@api-client/typeUtils";
import {
    DELIVERYTEAM_ORDER_STATUS_OBJ,
    isDeliveryteamOrderAllowedStatusChange,
    isDeliveryteamOrderAllowedStatusChange_noerror,
} from "@lib/types/deliveryteamOrderStatus";
import { ref, computed } from "vue";
import { toast } from "@/ui-plus/sonner";

const toastTopRight = { position: "top-right" as const };
import AcceptOrderModal from "./AcceptOrderModal.vue";
import { useDialog } from "@/ui-plus/dialog/use-dialog";
import ShowDeliveryteamOrder from "./ShowDeliveryteamOrder.vue";
import {
    STRIPE_PAYMENT_TYPE_ID,
    PAYPAL_PAYMENT_TYPE_ID,
    S_API_KEY_PAYMENT_TYPE_ID
} from "@lib/consts/paymentIds";
import { cachedDeliveryteamOrders } from "@deliveryteam-spa-orders-vue/refs/RecentOrders";
import {
    currentUserLat,
    currentUserLng,
    getCurrentUserLocationForOrderActions,
} from "@deliveryteam-spa-orders-vue/refs/UserCurrentLocation";
import DropoffIcon from "@/ui-plus/dropoff-options/DropoffIcon.vue";

const dialog = useDialog();

const ApiPath = deliveryteamOrdersApiClient[":deliveryteamId"].get_by_order_ids.$post;
type OrderType = ResType<typeof ApiPath>["data"];

const props = defineProps<{
    order: OrderType[number];
}>();

const order = computed(
    () =>
        cachedDeliveryteamOrders.value[props.order.deliveryteamOrderId] ??
        props.order,
);

const showRefundInfo = computed(() => {
    const o = order.value;
    return (
        o.paymentMethodJSON?.id === STRIPE_PAYMENT_TYPE_ID ||
        o.paymentMethodJSON?.id === PAYPAL_PAYMENT_TYPE_ID ||
        o.paymentMethodJSON?.id === S_API_KEY_PAYMENT_TYPE_ID
    );
});

let isAcceptingOrder = ref(false);
const handleAcceptApi = async (deliveryteamOrderId: string, deliveryteamId: string, minutes: number | undefined) => {
    if (isAcceptingOrder.value) {
        return;
    }
    isAcceptingOrder.value = true;
    const location = getCurrentUserLocationForOrderActions();
    if (!location) {
        toast.error("Current location not available yet. Please allow location access and try again.", toastTopRight);
        isAcceptingOrder.value = false;
        return;
    }
    const { error } = await (await deliveryteamOrdersApiClient[":deliveryteamId"].accept_order.$post({
        param: { deliveryteamId },
        json: {
            deliveryteamOrderId,
            estimatedTimeInMinutes: minutes,
            ...location,
        }
    })).json();
    if (error) {
        toast.error(error.message ?? "Error accepting order", toastTopRight);
    } else {
        toast.message(`Order accepted`, toastTopRight);
        isDeliveryteamOrderAllowedStatusChange(cachedDeliveryteamOrders.value[deliveryteamOrderId].orderStatus, DELIVERYTEAM_ORDER_STATUS_OBJ.ACCEPTED);
        cachedDeliveryteamOrders.value[deliveryteamOrderId].orderStatus = DELIVERYTEAM_ORDER_STATUS_OBJ.ACCEPTED;
        if (minutes) {
            cachedDeliveryteamOrders.value[deliveryteamOrderId].estimatedTimeJSON = {
                min: minutes,
                unit: 'minutes'
            }
        }
        // Update the timestamp to prevent the periodic refresh from reloading this order
        cachedDeliveryteamOrders.value[deliveryteamOrderId].updated_at_ts = Date.now();
    }
    isAcceptingOrder.value = false;
}
const handleAccept = async (deliveryteamOrderId: string, deliveryteamId: string) => {
    dialog.show<number | undefined>({
        title: "Accept Order",
        closeable: true,
        component: AcceptOrderModal,
        onSuccess: async (minutes) => {
            await handleAcceptApi(deliveryteamOrderId, deliveryteamId, minutes);
        },
        onError: async (error) => {
            console.log(error);
        }
    });

}

let isCancellingOrder = ref(false);
const handleCancel = async (deliveryteamOrderId: string, deliveryteamId: string) => {
    if (isCancellingOrder.value) {
        return;
    }
    isCancellingOrder.value = true;
    const order = cachedDeliveryteamOrders.value[deliveryteamOrderId];
    // 🚨Every month 2% of your orders can be cancelled free🚨 after that we may still charge you for the service fees.
    // ${order.paymentMethodJSON?.isOnline ? '\n🚨THIS ORDER HAS ONLINE PAYMENT🚨\n🚨It will be refunded to customer🚨\n' : ''}

    if (!confirm(`Are you sure you want to cancel this order?

If this order was paid online, the customer will be refunded.

🚨This action cannot be undone🚨`)) {
        isCancellingOrder.value = false;
        return;
    }

    // Implement cancel logic
    const location = getCurrentUserLocationForOrderActions();
    if (!location) {
        toast.error("Current location not available yet. Please allow location access and try again.", toastTopRight);
        isCancellingOrder.value = false;
        return;
    }
    const { error, data } = await (await deliveryteamOrdersApiClient[":deliveryteamId"].cancel_order.$post({
        param: { deliveryteamId },
        json: {
            deliveryteamOrderId,
            ...location,
        }
    })).json();
    if (error) {
        toast.error(error.message ?? "Error cancelling order", toastTopRight);
    } else {
        toast.message(`Order cancelled ${order.paymentMethodJSON?.isOnline ? 'and refunded!' : ''}`, toastTopRight);
        isDeliveryteamOrderAllowedStatusChange(cachedDeliveryteamOrders.value[deliveryteamOrderId].orderStatus, DELIVERYTEAM_ORDER_STATUS_OBJ.CANCELLED);
        cachedDeliveryteamOrders.value[deliveryteamOrderId].orderStatus = DELIVERYTEAM_ORDER_STATUS_OBJ.CANCELLED;
        // Update the timestamp to prevent the periodic refresh from reloading this order
        cachedDeliveryteamOrders.value[deliveryteamOrderId].updated_at_ts = Date.now();
        setTimeout(() => {
            if (data?.isTest) {
                toast.message(`System assumes this is a test order since the order was placed and cancelled by the same user. Fees added back to your deliveryteam balance`, toastTopRight);
            } else {
                if (data?.isFeeRefunded) {
                    toast.message(`Cancelled with no fee. Fees added back to your deliveryteam balance, use cancellation wisely, only 5% of orders can be cancelled free`, toastTopRight);
                } else {
                    toast.message(`${data?.cancelPercentage}% of orders were cancelled in the last 30 days, fees not refunded! (up to 5% of orders can be cancelled free)`, toastTopRight);
                }
            }
        }, 1000);
    }
    isCancellingOrder.value = false;
}

const PICKUP_SECONDARY_STATUSES = ['GOING_TO_PICKUP', 'WAITING_AT_PICKUP'] as const;
const DELIVERY_SECONDARY_STATUSES = ['GOING_TO_DELIVERY', 'WAITING_AT_DELIVERY'] as const;
const UNDELIVERABLE_SECONDARY_STATUSES = [
    'UNDELIVERABLE_LEAVE_AT_DOOR',
    'UNDELIVERABLE_DISCARDED',
    'UNDELIVERABLE_RETURNING_TO_PICKUP',
] as const;
const STATUS_CHANGE_REQUIRED_DISTANCE_METERS = 300;

type PostAcceptCourierStatus =
    | (typeof PICKUP_SECONDARY_STATUSES)[number]
    | (typeof DELIVERY_SECONDARY_STATUSES)[number]
    | (typeof UNDELIVERABLE_SECONDARY_STATUSES)[number]
    | 'PICKED_UP'
    | 'DELIVERED'
    | 'UNDELIVERABLE'
    | 'UNDELIVERABLE_COMPLETED';

type CourierStatusButtons = {
    primary: PostAcceptCourierStatus | undefined;
    secondary: PostAcceptCourierStatus[];
    showUndeliverable: boolean;
    showComplete: boolean;
};

type PendingStatusUpdate = {
    deliveryteamOrderId: string;
    deliveryteamId: string;
    newStatus: PostAcceptCourierStatus;
};

const proximityConfirmOpen = ref(false);
const proximityConfirmMessage = ref('');
const pendingStatusUpdate = ref<PendingStatusUpdate | null>(null);

const allowedSecondary = (
    currentStatus: OrderType[number]["orderStatus"],
    candidates: readonly PostAcceptCourierStatus[],
) =>
    candidates.filter(
        (status) =>
            status !== currentStatus &&
            isDeliveryteamOrderAllowedStatusChange_noerror(currentStatus, status),
    );

const courierStatusButtons = computed<CourierStatusButtons>(() => {
    const currentStatus = order.value.orderStatus;
    const undeliverableSecondary = allowedSecondary(
        currentStatus,
        UNDELIVERABLE_SECONDARY_STATUSES,
    );
    const showUndeliverable = isDeliveryteamOrderAllowedStatusChange_noerror(
        currentStatus,
        DELIVERYTEAM_ORDER_STATUS_OBJ.UNDELIVERABLE,
    );
    const isUndeliverableFlowStatus = [
        DELIVERYTEAM_ORDER_STATUS_OBJ.UNDELIVERABLE,
        DELIVERYTEAM_ORDER_STATUS_OBJ.UNDELIVERABLE_LEAVE_AT_DOOR,
        DELIVERYTEAM_ORDER_STATUS_OBJ.UNDELIVERABLE_DISCARDED,
        DELIVERYTEAM_ORDER_STATUS_OBJ.UNDELIVERABLE_RETURNING_TO_PICKUP,
    ].includes(currentStatus);
    const showComplete =
        isUndeliverableFlowStatus &&
        isDeliveryteamOrderAllowedStatusChange_noerror(
            currentStatus,
            DELIVERYTEAM_ORDER_STATUS_OBJ.UNDELIVERABLE_COMPLETED,
        );

    if (isDeliveryteamOrderAllowedStatusChange_noerror(currentStatus, 'PICKED_UP')) {
        return {
            primary: 'PICKED_UP',
            secondary: allowedSecondary(currentStatus, PICKUP_SECONDARY_STATUSES),
            showUndeliverable,
            showComplete,
        };
    }

    if (isDeliveryteamOrderAllowedStatusChange_noerror(currentStatus, 'DELIVERED')) {
        return {
            primary: 'DELIVERED',
            secondary: allowedSecondary(currentStatus, DELIVERY_SECONDARY_STATUSES),
            showUndeliverable,
            showComplete,
        };
    }

    if (isUndeliverableFlowStatus) {
        return {
            primary: undefined,
            secondary: undeliverableSecondary,
            showUndeliverable,
            showComplete,
        };
    }

    return {
        primary: undefined,
        secondary: [],
        showUndeliverable,
        showComplete,
    };
});

/** Extra courier actions in "More" (secondary statuses + Undeliverable). Finish task stays inline as a primary action. */
const hasMoreCourierMenu = computed(
    () =>
        courierStatusButtons.value.secondary.length > 0 ||
        courierStatusButtons.value.showUndeliverable,
);

const canAcceptOrder = computed(() =>
    isDeliveryteamOrderAllowedStatusChange_noerror(
        order.value.orderStatus,
        DELIVERYTEAM_ORDER_STATUS_OBJ.ACCEPTED,
    ),
);

const canCancelOrder = computed(() =>
    isDeliveryteamOrderAllowedStatusChange_noerror(
        order.value.orderStatus,
        DELIVERYTEAM_ORDER_STATUS_OBJ.CANCELLED,
    ),
);

const isPickupAddressDestructive = computed(() => {
    const currentStatus = order.value.orderStatus;
    return currentStatus.startsWith(DELIVERYTEAM_ORDER_STATUS_OBJ.UNDELIVERABLE);
});
const isPickupAddressPrimary = computed(() =>
    !isPickupAddressDestructive.value &&
    courierStatusButtons.value.primary === 'PICKED_UP',
);
const isDeliveryAddressPrimary = computed(() =>
    courierStatusButtons.value.primary === 'DELIVERED',
);
const totalTravelDistance = computed(() => {
    const pickup = order.value.pickupAddressJSON;
    const delivery = order.value.deliveryAddressJSON;

    if (
        pickup?.lat == null ||
        pickup.lng == null ||
        delivery?.lat == null ||
        delivery.lng == null
    ) {
        return undefined;
    }

    const measure = getMeasure(pickup.country ?? '');
    const distance = calculateDistance(
        { lat: pickup.lat, lng: pickup.lng },
        { lat: delivery.lat, lng: delivery.lng },
        measure,
    );

    return `${distance < 10 ? distance.toFixed(1) : Math.round(distance)} ${measure}`;
});

const checkBeforeUpdatingOrderStatus = (
    deliveryteamOrderId: string,
    newStatus: PostAcceptCourierStatus,
    // deliveryteamId: string,
) => {
    const order = cachedDeliveryteamOrders.value[deliveryteamOrderId];
    const currentStatus = order.orderStatus;
    const isAllowed = isDeliveryteamOrderAllowedStatusChange(currentStatus, newStatus);
    if (!isAllowed) {
        toast.error(`Order status not allowed to be changed from ${currentStatus} to ${newStatus}`, toastTopRight);
        return false;
    }
    return true;
}

const checkUserIsCloseToRequiredStatusChangeLocation = (
    deliveryteamOrderId: string,
    newStatus: PostAcceptCourierStatus,
) => {
    const order = cachedDeliveryteamOrders.value[deliveryteamOrderId];
    const userLat = currentUserLat.value;
    const userLng = currentUserLng.value;

    if (newStatus !== 'PICKED_UP' && newStatus !== 'DELIVERED') {
        return { isCloseEnough: true, message: '' };
    }

    const requiredLocation = newStatus === 'PICKED_UP'
        ? {
            label: 'pickup',
            lat: order.pickupAddressJSON?.lat ?? null,
            lng: order.pickupAddressJSON?.lng ?? null,
        }
        : {
            label: 'delivery',
            lat: order.deliveryAddressJSON?.lat ?? null,
            lng: order.deliveryAddressJSON?.lng ?? null,
        };

    if (
        userLat == null
        || userLng == null
        || requiredLocation.lat == null
        || requiredLocation.lng == null
    ) {
        return {
            isCloseEnough: false,
            message: `Your location or ${requiredLocation.label} location is missing. Continue anyway?`,
        };
    }

    const distanceKm = calculateDistance(
        { lat: userLat, lng: userLng },
        { lat: requiredLocation.lat, lng: requiredLocation.lng },
        'km',
    );
    const distanceMeters = Math.round(distanceKm * 1000);
    const isCloseEnough = distanceMeters <= STATUS_CHANGE_REQUIRED_DISTANCE_METERS;

    return {
        isCloseEnough,
        message: `You are about ${distanceMeters}m away from the ${requiredLocation.label} location. Continue anyway?`,
    };
};

let isUpdatingOrderStatus = ref(false);
const updateOrderStatusApi = async (
    deliveryteamOrderId: string,
    deliveryteamId: string,
    newStatus: PostAcceptCourierStatus
) => {
    if (!newStatus || isUpdatingOrderStatus.value) {
        return;
    }
    isUpdatingOrderStatus.value = true;
    const location = getCurrentUserLocationForOrderActions();
    if (!location) {
        toast.error("Current location not available yet. Please allow location access and try again.", toastTopRight);
        isUpdatingOrderStatus.value = false;
        return;
    }
    const { error } = await (await deliveryteamOrdersApiClient[":deliveryteamId"].update_order_status.$post({
        param: { deliveryteamId },
        json: {
            deliveryteamOrderId,
            newStatus,
            ...location,
        }
    })).json();
    if (error) {
        toast.error(error.message ?? "Error updating status", toastTopRight);
    } else {
        toast.message(`Order status updated to ${newStatus}`, toastTopRight);
        // isDeliveryteamOrderAllowedStatusChange(cachedDeliveryteamOrders.value[deliveryteamOrderId].orderStatus, newStatus);
        cachedDeliveryteamOrders.value[deliveryteamOrderId].orderStatus = newStatus;
        cachedDeliveryteamOrders.value[deliveryteamOrderId].updated_at_ts = Date.now();
    }
    isUpdatingOrderStatus.value = false;
}

const openProximityConfirmation = (
    deliveryteamOrderId: string,
    deliveryteamId: string,
    newStatus: PostAcceptCourierStatus,
    message: string,
) => {
    pendingStatusUpdate.value = {
        deliveryteamOrderId,
        deliveryteamId,
        newStatus,
    };
    proximityConfirmMessage.value = message;
    proximityConfirmOpen.value = true;
};

const handleUpdateStatus = async (
    deliveryteamOrderId: string,
    deliveryteamId: string,
    newStatus: PostAcceptCourierStatus
) => {
    if (!checkBeforeUpdatingOrderStatus(deliveryteamOrderId, newStatus)) {
        return;
    }

    const proximityCheck = checkUserIsCloseToRequiredStatusChangeLocation(
        deliveryteamOrderId,
        newStatus,
    );
    if (!proximityCheck.isCloseEnough) {
        openProximityConfirmation(
            deliveryteamOrderId,
            deliveryteamId,
            newStatus,
            proximityCheck.message,
        );
        return;
    }

    await updateOrderStatusApi(deliveryteamOrderId, deliveryteamId, newStatus);
};

const handleConfirmProximityOverride = async () => {
    const pending = pendingStatusUpdate.value;
    proximityConfirmOpen.value = false;
    if (!pending) return;
    pendingStatusUpdate.value = null;
    await updateOrderStatusApi(
        pending.deliveryteamOrderId,
        pending.deliveryteamId,
        pending.newStatus,
    );
};

const handleCancelProximityOverride = () => {
    proximityConfirmOpen.value = false;
    pendingStatusUpdate.value = null;
};

/** One entry point for "could not complete delivery"; further resolution stays between store and buyer. */
const handleMarkUndeliverable = async (
    deliveryteamOrderId: string,
    deliveryteamId: string,
) => {
    if (
        !confirm(
            `Are you sure? \n\nAfter marking as undeliverable, you cannot mark this order as delivered.`,

        )
    ) {
        return;
    }
    await handleUpdateStatus(deliveryteamOrderId, deliveryteamId, 'UNDELIVERABLE');
};

const handleCompleteUndeliverable = async (
    deliveryteamOrderId: string,
    deliveryteamId: string,
) => {
    if (
        !confirm(
            `Mark as completed only if the delivery is fully finished.\n\nDon't complete if returning to pickup location.`,
        )
    ) {
        return;
    }
    await handleUpdateStatus(
        deliveryteamOrderId,
        deliveryteamId,
        'UNDELIVERABLE_COMPLETED',
    );
};

const IS_DEV = import.meta.env.DEV;

</script>
<template>
    <Card class="">
        <CardContent class="p-0">
            <div class="flex flex-col justify-between">
                <Dialog>
                    <DialogScrollContent class="w-fit pt-8">
                        <DialogHeader>
                            <DialogTitle>
                                Order #{{ order.shortOtp || '' }}
                            </DialogTitle>
                            <DialogDescription />
                            <div class="flex gap-2 justify-between">
                                <Button class="animate__tada"
                                        size="lg"
                                        :disabled="isAcceptingOrder"
                                        v-if="canAcceptOrder"
                                        @click="handleAccept(order.deliveryteamOrderId, order.deliveryteamId)">
                                    <CheckCircle class="h-4 w-4 mr-1" />
                                    Accept
                                </Button>
                            </div>
                            <div class="flex flex-wrap gap-2"
                                 v-if="courierStatusButtons.primary || hasMoreCourierMenu || courierStatusButtons.showComplete">
                                <template v-if="courierStatusButtons.primary">
                                    <Button size="lg"
                                            :disabled="isUpdatingOrderStatus"
                                            @click="handleUpdateStatus(order.deliveryteamOrderId, order.deliveryteamId, courierStatusButtons.primary)">
                                        {{ courierStatusButtons.primary }}
                                    </Button>
                                </template>
                                <Button v-if="courierStatusButtons.showComplete"
                                        size="lg"
                                        :disabled="isUpdatingOrderStatus"
                                        @click="handleCompleteUndeliverable(order.deliveryteamOrderId, order.deliveryteamId)">
                                    Finish task
                                </Button>
                                <DropdownMenu v-if="hasMoreCourierMenu">
                                    <DropdownMenuTrigger as-child>
                                        <Button type="button"
                                                variant="outline"
                                                size="sm"
                                                :disabled="isUpdatingOrderStatus">
                                            <MoreHorizontal class="h-4 w-4" />
                                            More
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start"
                                                         class="max-h-72 overflow-y-auto">
                                        <DropdownMenuItem v-for="secondaryStatus in courierStatusButtons.secondary"
                                                          :key="`dialog-${order.deliveryteamOrderId}-${secondaryStatus}`"
                                                          class="cursor-pointer"
                                                          :disabled="isUpdatingOrderStatus"
                                                          @click="
                                                            handleUpdateStatus(
                                                                order.deliveryteamOrderId,
                                                                order.deliveryteamId,
                                                                secondaryStatus,
                                                            )
                                                            ">
                                            {{ secondaryStatus }}
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator v-if="
                                            courierStatusButtons.secondary.length > 0 &&
                                            courierStatusButtons.showUndeliverable
                                        " />
                                        <DropdownMenuItem v-if="courierStatusButtons.showUndeliverable"
                                                          class="cursor-pointer text-destructive focus:bg-destructive focus:text-destructive-foreground"
                                                          :disabled="isUpdatingOrderStatus"
                                                          @click="handleMarkUndeliverable(order.deliveryteamOrderId, order.deliveryteamId)">
                                            Undeliverable
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </DialogHeader>

                        <ShowDeliveryteamOrder :orderDetails="order" />

                        <div class="flex gap-2 justify-between">
                            <Button size="lg"
                                    class="w-full animate__tada"
                                    :disabled="isAcceptingOrder"
                                    v-if="canAcceptOrder"
                                    @click="handleAccept(order.deliveryteamOrderId, order.deliveryteamId)">
                                <CheckCircle class="h-4 w-4 mr-1" />
                                Accept
                            </Button>
                            <!-- <br>
                                            order.userId: {{ order.userId }}
                                            <br>
                                            user.id: {{ user?.id }}
                                            <br> -->
                            <div class="flex gap-2 items-center">
                                <DropdownMenu v-if="canCancelOrder">
                                    <DropdownMenuTrigger as-child>
                                        <Button variant="destructive"
                                                size="sm">
                                            <XCircle /> {{ showRefundInfo ? 'Refund' : 'Cancel' }} Order
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start">
                                        <DropdownMenuItem :disabled="isCancellingOrder"
                                                          @click="handleCancel(order.deliveryteamOrderId, order.deliveryteamId)"
                                                          class="bg-destructive text-destructive-foreground">
                                            <div>
                                                <p class="text-2xl font-bold">Are you sure?</p>
                                            </div>
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                <Popover v-if="canCancelOrder">
                                    <PopoverTrigger as-child>
                                        <Info class="inline-block" />
                                    </PopoverTrigger>
                                    <PopoverContent align="start">
                                        <div class="space-y-2">
                                            <p class="text-2xl font-bold">Are you sure to cancel?</p>
                                            <p class="text-xs my-4">
                                                If it was a test order,
                                                <br>
                                                make sure to cancel it in the same browser.
                                                <br>
                                                AutoPrint app can not detect test orders.
                                            </p>
                                            <p class="text-xs my-4">
                                                Use cancellation wisely,
                                                <br>
                                                only 5% of orders can be cancelled free.
                                            </p>
                                            <p class="text-xs my-4">
                                                Fees will not be refunded
                                                <br>
                                                if more than 5% of orders were
                                                <br>
                                                cancelled in the last 30 days.
                                            </p>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <a class="text-xs underline"
                               v-if="!IS_DEV && order.paymentMethodJSON?.isOnline &&
                                order.stripeDeliveryteamConnectAccountId &&
                                order.stripePaymentIntentId"
                               :href="`https://dashboard.stripe.com/${order.stripeDeliveryteamConnectAccountId}/payments/${order.stripePaymentIntentId}`"
                               target="_blank">
                                See payment in stripe dashboard
                            </a>
                            <a class="text-xs underline"
                               v-if="IS_DEV && order.paymentMethodJSON?.isOnline &&
                                order.stripeDeliveryteamConnectAccountId &&
                                order.stripePaymentIntentId"
                               :href="`https://dashboard.stripe.com/acct_1RIvHDGftdUvJams/test/connect/accounts/${order.stripeDeliveryteamConnectAccountId}/payments/${order.stripePaymentIntentId}`"
                               target="_blank">
                                See payment in stripe dashboard
                            </a>
                            <a class="text-xs underline"
                               v-if="IS_DEV && order.sApiKey && order.sApiKey.sApiKeyPaymentIntentId"
                               :href="`https://dashboard.stripe.com/acct_1UC3b5K6oPMjQGYC/test/payments/${order.sApiKey.sApiKeyPaymentIntentId}`"
                               target="_blank">
                                See payment in stripe dashboard
                            </a>
                        </div>
                    </DialogScrollContent>

                    <!-- Order Info -->
                    <DialogTrigger as-child
                                   class="w-full cursor-pointer">
                        <div :class="[canAcceptOrder ? 'ripple-elem' : '']">
                            <div
                                 class="border-t-2 flex flex-wrap items-center sticky top-0 z-10 bg-card rounded-md p-2">
                                <h3 class="text-lg font-bold">#{{ order.shortOtp ||
                                    'N/A' }}</h3>
                                <Badge
                                       :variant="`${order.orderStatus === DELIVERYTEAM_ORDER_STATUS_OBJ.CANCELLED || order.orderStatus.startsWith(DELIVERYTEAM_ORDER_STATUS_OBJ.UNDELIVERABLE) ? 'destructive' : 'secondary'}`">
                                    {{ order.orderStatus }}
                                </Badge>
                                <div v-if="order.scheduled_at_ts">
                                    <Badge variant="secondary"
                                           class="capitalize text-xs">
                                        SCHEDULED
                                    </Badge>
                                </div>
                                <span v-if="order.isCancelled"
                                      class="text-xs border border-destructive-foreground p-1 rounded-md inline-block m-1 bg-destructive text-destructive-foreground underline">
                                    Order CANCELLED
                                </span>
                            </div>
                            <div class="text-left text-2xl font-bold p-2 border-b border-t">
                                <span>
                                    {{ formatCurrency(order.finalAmount, order.taxSettingsJSON?.currency) }}
                                </span>
                                <span v-if="totalTravelDistance"
                                      class="ml-1 font-bold text-xs">
                                    ({{ totalTravelDistance }})
                                </span>
                            </div>

                            <div class="text-left text-xs space-y-2 bg-muted/20 p-2">
                                <!-- Pickup address -->
                                <div class="flex gap-2 rounded-md p-1.5 transition-colors"
                                     :class="[
                                        isPickupAddressDestructive
                                            ? 'border border-destructive/50 bg-destructive/10'
                                            : isPickupAddressPrimary
                                                ? 'border border-primary/50 bg-primary/10'
                                                : '',
                                    ]">
                                    <div
                                         class="flex flex-col items-center justify-center gap-1 min-w-14 max-w-14 w-14 border-e border-muted-foreground pr-2">
                                        <div class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                                             :class="[
                                                isPickupAddressDestructive
                                                    ? 'bg-destructive text-destructive-foreground'
                                                    : isPickupAddressPrimary
                                                        ? 'bg-primary text-primary-foreground'
                                                        : 'bg-primary/10 text-primary',
                                            ]">
                                            <MapPin class="h-3.5 w-3.5" />
                                        </div>
                                        <p class="text-[10px] font-semibold uppercase tracking-wide"
                                           :class="[
                                            isPickupAddressDestructive
                                                ? 'text-destructive'
                                                : isPickupAddressPrimary
                                                    ? 'text-primary'
                                                    : 'text-muted-foreground',
                                        ]">
                                            Pickup
                                        </p>
                                    </div>
                                    <div class="min-w-0 flex-1">
                                        <p class="min-w-0 max-w-full overflow-x-auto overflow-y-hidden whitespace-nowrap font-medium"
                                           :class="[
                                            isPickupAddressDestructive
                                                ? 'text-destructive'
                                                : isPickupAddressPrimary
                                                    ? 'text-primary'
                                                    : 'text-foreground',
                                        ]">
                                            {{
                                                order.pickupAddressJSON?.shippingName ||
                                                order.userName ||
                                                order.userEmail || 'unknown'
                                            }}
                                        </p>
                                        <p
                                           class="min-w-0 max-w-full overflow-x-auto overflow-y-hidden whitespace-nowrap">
                                            {{ order.pickupAddressJSON?.address1 }}
                                            {{ order.pickupAddressJSON?.address2 }}
                                            {{ order.pickupAddressJSON?.instructionsForPickupDelivery }}
                                            <!-- {{ order.pickupAddressJSON?.city }}
                                            {{ order.pickupAddressJSON?.state }}
                                            {{ order.pickupAddressJSON?.postalCode }}
                                            {{ order.pickupAddressJSON?.country }} -->
                                        </p>
                                        <div v-if="order.pickupAddressJSON?.instructionCategory"
                                             class="min-w-0 max-w-full overflow-x-auto overflow-y-hidden whitespace-nowrap">
                                            <div style="transform: scale(0.7);transform-origin: left center;"
                                                 class="flex items-center text-sm gap-4 text-foreground">
                                                <DropoffIcon :option="order.pickupAddressJSON?.instructionCategory" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Delivery address -->
                                <div class="flex gap-2 rounded-md p-1.5 transition-colors"
                                     :class="[
                                        isDeliveryAddressPrimary
                                            ? 'border border-primary/50 bg-primary/10'
                                            : 'border-t pt-2',
                                    ]">
                                    <div
                                         class="flex flex-col items-center justify-center gap-1 min-w-14 max-w-14 w-14 border-e border-muted-foreground pr-2">
                                        <div class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                                             :class="[
                                                isDeliveryAddressPrimary
                                                    ? 'bg-primary text-primary-foreground'
                                                    : 'bg-secondary text-secondary-foreground',
                                            ]">
                                            <Navigation class="h-3.5 w-3.5" />
                                        </div>
                                        <p class="text-[10px] font-semibold uppercase tracking-wide"
                                           :class="[
                                            isDeliveryAddressPrimary
                                                ? 'text-primary'
                                                : 'text-muted-foreground',
                                        ]">
                                            Delivery
                                        </p>
                                    </div>
                                    <div class="min-w-0 flex-1">
                                        <p class="min-w-0 max-w-full overflow-x-auto overflow-y-hidden whitespace-nowrap font-medium"
                                           :class="[
                                            isDeliveryAddressPrimary
                                                ? 'text-primary'
                                                : 'text-foreground',
                                        ]">
                                            {{
                                                order.deliveryAddressJSON?.shippingName ||
                                                order.userName ||
                                                order.userEmail || 'unknown'
                                            }}
                                        </p>
                                        <p
                                           class="min-w-0 max-w-full overflow-x-auto overflow-y-hidden whitespace-nowrap">
                                            {{ order.deliveryAddressJSON?.address1 }}
                                            {{ order.deliveryAddressJSON?.address2 }}
                                            {{ order.deliveryAddressJSON?.instructionsForPickupDelivery }}
                                            <!-- {{ order.deliveryAddressJSON?.city }}
                                            {{ order.deliveryAddressJSON?.state }}
                                            {{ order.deliveryAddressJSON?.postalCode }}
                                            {{ order.deliveryAddressJSON?.country }} -->
                                        </p>
                                        <div v-if="order.deliveryAddressJSON?.instructionCategory"
                                             class="min-w-0 max-w-full overflow-x-auto overflow-y-hidden whitespace-nowrap">
                                            <div style="transform: scale(0.7);transform-origin: left center;"
                                                 class="flex items-center text-sm gap-4 text-foreground">
                                                <DropoffIcon :option="order.deliveryAddressJSON?.instructionCategory" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex min-w-0 items-center gap-2 border-t -mx-2 pt-2 px-2 text-[11px]">
                                    <Clock class="h-3.5 w-3.5 shrink-0" />
                                    <span class="min-w-0 flex-1 overflow-x-auto overflow-y-hidden whitespace-nowrap">
                                        Placed {{ formatDate(order.order_placed_at_ts, undefined, true) }}
                                        <span v-if="order.order_placed_at_ts !== order.draft_create_at_ts">
                                            <br>
                                            Draft: {{ formatDate(order.draft_create_at_ts, undefined, true) }}
                                        </span>
                                        <span v-if="order.scheduled_at_ts">
                                            <br>
                                            Scheduled: {{ formatDate(order.scheduled_at_ts, undefined, true) }}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>

                    </DialogTrigger>

                    <!-- Accept + primary + Finish task inline; secondary + Undeliverable in More ▾ -->
                    <!-- <div class="relative"> -->
                    <div
                         class="sticky relative bottom-0 left-0 right-0 z-10 min-w-0 max-w-full overflow-x-auto overflow-y-hidden whitespace-nowrap">
                        <Button size="lg"
                                v-if="canAcceptOrder"
                                class="action-btn min-h-9 w-full"
                                :disabled="isAcceptingOrder"
                                @click="handleAccept(order.deliveryteamOrderId, order.deliveryteamId)">
                            <span class="animate__tada font-bold text-2xl">
                                <CheckCircle class="mr-1 inline-block min-h-6 min-w-6" />
                                Accept
                            </span>
                        </Button>
                        <!-- <DialogTrigger as-child>
                            <Button variant="outline"
                                    size="sm"
                                    class="action-btn min-h-8 shrink-0 max-sm:min-h-9 max-sm:flex-1 max-sm:basis-[calc(50%-0.375rem)] max-sm:justify-center max-sm:text-xs">
                                Show
                            </Button>
                        </DialogTrigger> -->
                        <div class="flex w-full flex-row"
                             v-if="courierStatusButtons.primary || hasMoreCourierMenu || courierStatusButtons.showComplete">
                            <Button v-if="courierStatusButtons.primary"
                                    size="lg"
                                    class="action-btn min-h-9 w-full"
                                    :disabled="isUpdatingOrderStatus"
                                    @click="handleUpdateStatus(order.deliveryteamOrderId, order.deliveryteamId, courierStatusButtons.primary)">
                                {{ courierStatusButtons.primary }}
                            </Button>
                            <Button v-if="courierStatusButtons.showComplete"
                                    size="lg"
                                    class="action-btn min-h-9 w-full"
                                    :disabled="isUpdatingOrderStatus"
                                    @click="handleCompleteUndeliverable(order.deliveryteamOrderId, order.deliveryteamId)">
                                Finish task
                            </Button>
                            <DropdownMenu v-if="hasMoreCourierMenu">
                                <DropdownMenuTrigger as-child>
                                    <Button type="button"
                                            size="lg"
                                            variant="outline"
                                            class="action-btn min-h-9"
                                            :disabled="isUpdatingOrderStatus">
                                        <MoreHorizontal class="h-4 w-4" />
                                        More
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end"
                                                     class="max-h-72 max-w-[min(100vw-2rem,18rem)] overflow-y-auto">
                                    <DropdownMenuItem v-for="secondaryStatus in courierStatusButtons.secondary"
                                                      :key="`card-more-${order.deliveryteamOrderId}-${secondaryStatus}`"
                                                      class="cursor-pointer"
                                                      :disabled="isUpdatingOrderStatus"
                                                      @click="
                                                        handleUpdateStatus(
                                                            order.deliveryteamOrderId,
                                                            order.deliveryteamId,
                                                            secondaryStatus,
                                                        )
                                                        ">
                                        {{ secondaryStatus }}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator v-if="
                                        courierStatusButtons.secondary.length > 0 &&
                                        courierStatusButtons.showUndeliverable
                                    " />
                                    <DropdownMenuItem v-if="courierStatusButtons.showUndeliverable"
                                                      class="cursor-pointer text-destructive focus:bg-destructive focus:text-destructive-foreground"
                                                      :disabled="isUpdatingOrderStatus"
                                                      @click="handleMarkUndeliverable(order.deliveryteamOrderId, order.deliveryteamId)">
                                        Undeliverable
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>

                        <!-- <Popover v-if="IS_DEV">
                            <PopoverTrigger as-child>
                                <Button variant="ghost"
                                        size="sm"
                                        class="action-btn min-h-8 shrink-0 max-sm:min-h-9 max-sm:w-[calc(50%-0.375rem)] max-sm:justify-center max-sm:px-0 sm:h-8 sm:w-auto">
                                    <Eye class="h-4 w-4" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent class="max-w-xl w-full max-h-96 overflow-y-auto">
                                <pre class="text-xs">{{ order }}</pre>
                            </PopoverContent>
                        </Popover> -->
                    </div>
                    <!-- </div> -->
                </Dialog>
            </div>
        </CardContent>

        <AlertDialog v-model:open="proximityConfirmOpen">
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>You are not near required location</AlertDialogTitle>
                    <AlertDialogDescription>
                        {{ proximityConfirmMessage }}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel @click="handleCancelProximityOverride">Cancel</AlertDialogCancel>
                    <AlertDialogAction @click="handleConfirmProximityOverride">Continue</AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    </Card>
</template>